import { gsap } from 'gsap';
import EventBus from 'eventing-bus';
import { useDispatch, useSelector } from 'react-redux';
import { SplitText } from 'gsap/SplitText';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import React, { useRef, useState, useEffect } from 'react';
import { Link, withRouter, useLocation } from 'react-router-dom';
import { ValidatorForm, TextValidator } from 'react-material-ui-form-validator';

import logo from '../../static/img/logo.png';
import { resetPassword } from '../../store/Auth';
import { getBranding } from '../../store/Settings';

gsap.registerPlugin(SplitText, ScrollTrigger);
const initialState = { code: '', password: '', confirmPassword: '' }
const ResetPassword = ({ history }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const headingRefs = useRef([]);
  const paragraphRefs = useRef([]);
  const splitParagraphs = useRef([]);
  const { currentBranding } = useSelector(state => state['Settings']);
  
  const [loading, setLoading] = useState(false);
  const [state, setState] = useState(initialState);
  const [showPassword, setShowPassword] = useState(false);
  const [key, setEmailCode] = useState(['', '', '', '', '', '']);
  const emailInputRefs = Array.from({ length: 6 }, () => useRef(null));
  const togglePasswordVisibility = field => setShowPassword(prev => ({ ...prev, [field]: !prev[field] }));

  useEffect(() => { document.title = 'Reset Password | ' + currentBranding?.['agencyName'] }, [currentBranding]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const code = searchParams.get('code');

    if (code) {
      const newKey = ['', '', '', '', '', ''];
      const codeDigits = code.slice(0, 6).split('');
      codeDigits.forEach((digit, index) => { if (index < 6 && /^\d$/.test(digit)) newKey[index] = digit })
      setEmailCode(newKey);

      const firstEmptyIndex = newKey.findIndex(digit => digit === '');
      const focusIndex = firstEmptyIndex !== -1 ? firstEmptyIndex : 5;
      setTimeout(() => { if (emailInputRefs[focusIndex]?.current) emailInputRefs[focusIndex].current.focus() }, 100)
    }
  }, [location.search])

  const handleEmailInputChange = (index, value) => {
    if (value.length > 1) value = value.slice(value.length - 1)
    setEmailCode(st => st.map((_, idx) => idx === index ? value : _))
    if (value.length === 1 && index < 5) emailInputRefs[index + 1].current.focus()
  }

  const handlePaste = (e) => {
    e.preventDefault()
    const pastedData = e.clipboardData.getData('text/plain').replace(/\D/g, '').slice(0, 6)
    if (pastedData) {
      const newKey = [...key]
      const digits = pastedData.split('')
      digits.forEach((digit, index) => {
        if (index < emailInputRefs.length) {
          newKey[index] = digit
          if (index === digits.length - 1 && index < emailInputRefs.length - 1) emailInputRefs[index + 1].current.focus()
        }
      })
      setEmailCode(newKey)
    }
  }

  const handleChange = e => setState(st => ({ ...st, [e['target'] && e['target']['name']]: e['target'] && e['target']['value'] }))
  const handleLogin = () => {
    if (state['password'] != state['confirmPassword']) return EventBus.publish('error', 'Passwords does not match')

    setLoading(true)
    dispatch(resetPassword({
      formData: { password: state['password'], key: +`${key.join('')}` },
      successCallback: () => {
        setLoading(false)
        setState(initialState)
        history.push('/login')
      }, failCallback: () => setLoading(false)
    }))
  }

  useEffect(() => {
      // Reset any previous SplitText instances
      splitParagraphs.current.forEach((split) => split?.revert?.());
      splitParagraphs.current = [];
  
      // Animate each heading
      headingRefs.current.forEach((el, i) => {
        gsap.fromTo(
          el,
          { opacity: 0, filter: "blur(10px)", y: -20 },
          {
            opacity: 1,
            filter: "blur(0px)",
            y: 0,
            duration: 0.8,
            ease: "power3.out",
            delay: i * 0.3,
          }
        );
      });
  
      // Animate each paragraph with SplitText
      paragraphRefs.current.forEach((el, i) => {
        const split = new SplitText(el, { type: "chars" });
        splitParagraphs.current.push(split);
  
        gsap.fromTo(
          split.chars,
          { opacity: 0, y: 20 },
          {
            opacity: 1,
            y: 0,
            duration: 1,
            stagger: 0.02,
            ease: "power2.out",
            delay: (headingRefs.current.length + i) * 0.1,
          }
        );
      });
    }, []);
  

  return (
    <div className='login-page'>
      <div className='logo-area'>
        { currentBranding?.['logo'] && <img className='login-page-logo' src={currentBranding?.['logo']} alt={currentBranding?.['agencyName']} /> }
      </div>
      <div className='auto-container'>
        <div className='row'>
          <div className='col-lg-6 col-md-6 col-sm-12 login-area'>
            <div className='left-img-area forgot-password-img'>
              <img src={require('../../static/img/forgot-password.png')} alt='' />
              <h2 ref={(el) => el && headingRefs.current.push(el)}>{currentBranding?.['agencyName']}</h2>
              <p ref={(el) => el && paragraphRefs.current.push(el)}>Access predictive AI insights, automated follow-ups<br />and always-on AI assistance.</p>
            </div>
          </div>
          <div className='col-lg-5 offset-lg-1 col-md-6 col-sm-12 login-area'>
            <div className='login-form'>
              <div className='title-area'>
                <h2 ref={(el) => el && headingRefs.current.push(el)}>Confirm Code</h2>
                <p ref={(el) => el && paragraphRefs.current.push(el)}>Verify your identity with the 6-digit code, create a new password, and resume using your account.</p>
              </div>
              <ValidatorForm className='validator-form mt-4' onSubmit={handleLogin}>
                <div className='group-form'>
                  <label>Email Code</label>
                  <div className='authentication-group email-code-group'>
                    {emailInputRefs.map((ref, index) => (
                      <TextValidator
                        min={0}
                        fullWidth
                        required
                        key={index}
                        type='number'
                        inputRef={ref}
                        margin='dense'
                        placeholder=''
                        variant='outlined'
                        value={key[index]}
                        className='login-input-fields'
                        name={`EmailCodeEnter${index}`}
                        onPaste={index === 0 ? handlePaste : undefined}
                        onChange={e => handleEmailInputChange(index, e['target']['value'])}
                      />
                    ))}
                  </div>
                </div>
                <div className='group-form password-group'>
                  <label>New Password</label>
                  <TextValidator
                    required
                    margin='dense'
                    name='password'
                    variant='outlined'
                    onChange={handleChange}
                    value={state['password']}
                    className='login-input-fields'
                    placeholder='New Password here'
                    type={showPassword['password'] ? 'text' : 'password'}
                  />
                  <button type='button' className='toggle-password-visibility' onClick={() => togglePasswordVisibility('password')}>
                    <i className={`fa ${showPassword['password'] ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                  </button>
                </div>
                <div className='group-form password-group'>
                  <label>Confirm Password</label>
                  <TextValidator
                    required
                    margin='dense'
                    variant='outlined'
                    name='confirmPassword'
                    onChange={handleChange}
                    className='login-input-fields'
                    value={state['confirmPassword']}
                    placeholder='Confirm Password here'
                    type={showPassword['confirmPassword'] ? 'text' : 'password'}
                  />
                  <button type='button' className='toggle-password-visibility' onClick={() => togglePasswordVisibility('confirmPassword')}>
                    <i className={`fa ${showPassword['confirmPassword'] ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                  </button>
                </div>

                <div className='group-form text-center'>
                  <button type='submit' className='btn-style-new' disabled={loading}>
                    {!loading ? 'Reset Password' : <div><i className='fa fa-spinner fa-spin'></i></div>}
                  </button>
                </div>
                <div className='group-form checkbox-group'>
                  <Link className='forget-pass' to='/login'>Login ?</Link>
                </div>
              </ValidatorForm>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};


export default (withRouter(ResetPassword));